import React from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { useParams } from "react-router-dom";
import { format } from "date-fns";
import Table from "components/Table/Table";
import { PaginationBar } from "components/Pagination";
import PageSizeSelect from "components/Table/PageSizeSelect";
import { useTableView } from "components/Table/useTableView";
import { useAuth0 } from "@auth0/auth0-react";
import { useActiveOrg } from "context/ActiveOrgContext";
import Button from "components/Button/Regular";
import Input from "components/Input/Input";
import Select from "components/Input/Select";
import Text from "components/Input/Text";
import ConfirmDialog from "components/ConfirmDialog";
import vgToast from "utils/toast";
import {
  createMedicalNote,
  deleteMedicalNote,
  readMedicalRecordByOrgAndAnimalId,
  updateMedicalNote,
} from "services/patients.service";
import {
  buildMedicalNoteDescription,
  getMedicalNoteTypeLabel,
  isHeightNote,
  isPulseNote,
  isRespirationNote,
  isTemperatureNote,
  isWeightNote,
  medicalNoteTypeOptions,
  parseMedicalNoteDescription,
  temperatureUnitOptions,
  toSelectOptions,
  weightUnitOptions,
  heightUnitOptions,
} from "constants/medicalNoteEnums";

type Row = {
  id: string;
  date: string;
  type: string;
  description: string;
};

type MedicalNoteRow = {
  id: string;
  medicalRecordId: number;
  medicalNoteId: number;
  createdOn_UTC?: string;
  medicalNoteType: number;
  noteDescription: string;
};

type FormValues = {
  date: string;
  time: string;
  medicalNoteType: string;
  noteDescription: string;
  weightMeasurement: string;
  weightUnit: string;
  heightMeasurement: string;
  heightUnit: string;
  temperatureMeasurement: string;
  temperatureUnit: string;
  pulseMeasurement: string;
  respirationMeasurement: string;
};

function mapNoteToRow(raw: unknown): MedicalNoteRow {
  const row = raw as Record<string, unknown>;
  return {
    id: String(row.medicalNoteId ?? row.MedicalNoteId ?? ""),
    medicalRecordId: Number(row.medicalRecordId ?? row.MedicalRecordId ?? 0),
    medicalNoteId: Number(row.medicalNoteId ?? row.MedicalNoteId ?? 0),
    createdOn_UTC: row.createdOn_UTC
      ? String(row.createdOn_UTC)
      : row.CreatedOn_UTC
        ? String(row.CreatedOn_UTC)
        : undefined,
    medicalNoteType: Number(row.medicalNoteType ?? row.MedicalNoteType ?? 0),
    noteDescription: String(row.noteDescription ?? row.NoteDescription ?? ""),
  };
}

function formatRow(row: MedicalNoteRow): Row {
  const created = row.createdOn_UTC ? new Date(row.createdOn_UTC) : null;
  return {
    id: row.id,
    date:
      created && !Number.isNaN(created.getTime())
        ? created.toLocaleString()
        : row.createdOn_UTC ?? "",
    type: getMedicalNoteTypeLabel(row.medicalNoteType),
    description: row.noteDescription,
  };
}

function toDatePart(value: string | undefined) {
  if (!value) return format(new Date(), "yyyy-MM-dd");
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? format(new Date(), "yyyy-MM-dd") : format(d, "yyyy-MM-dd");
}

function toTimePart(value: string | undefined) {
  if (!value) return format(new Date(), "HH:mm");
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? format(new Date(), "HH:mm") : format(d, "HH:mm");
}

function buildCreatedOnUtc(date: string, time: string) {
  if (!date) return undefined;
  const isoLike = `${date}T${time || "00:00"}:00`;
  const parsed = new Date(isoLike);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString();
}

const MedicalNotesList = () => {
  const { id } = useParams();
  const { orgId } = useActiveOrg();
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  const animalId = id ? Number(id) : NaN;

  const [notes, setNotes] = React.useState<MedicalNoteRow[]>([]);
  const [medicalRecordId, setMedicalRecordId] = React.useState<number>(0);
  const [selectedNoteId, setSelectedNoteId] = React.useState<number | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);
  const [pendingDeleteNoteId, setPendingDeleteNoteId] = React.useState<number | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    reset,
  } = useForm<FormValues>({
    defaultValues: {
      date: format(new Date(), "yyyy-MM-dd"),
      time: format(new Date(), "HH:mm"),
      medicalNoteType: "2",
      noteDescription: "",
      weightMeasurement: "",
      weightUnit: "kg",
      heightMeasurement: "",
      heightUnit: "m",
      temperatureMeasurement: "",
      temperatureUnit: "celcius",
      pulseMeasurement: "",
      respirationMeasurement: "",
    },
  });

  const load = React.useCallback(async () => {
    if (!isAuthenticated || orgId <= 0 || !Number.isFinite(animalId) || animalId <= 0) {
      setNotes([]);
      setMedicalRecordId(0);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      const { data, error: apiError } = await readMedicalRecordByOrgAndAnimalId(
        token,
        orgId,
        animalId
      );
      if (apiError) {
        setError(apiError.message);
        setNotes([]);
        setMedicalRecordId(0);
        return;
      }
      const mapped = (data?.medicalNotes ?? []).map(mapNoteToRow);
      setNotes(mapped);
      setMedicalRecordId(
        Number(data?.medicalRecordId ?? data?.MedicalRecordId ?? mapped[0]?.medicalRecordId ?? 0)
      );
    } catch (e) {
      setError((e as Error).message);
      setNotes([]);
      setMedicalRecordId(0);
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, isAuthenticated, orgId, animalId]);

  React.useEffect(() => {
    load();
  }, [load]);

  const resetToCreateMode = React.useCallback(() => {
    setSelectedNoteId(null);
    reset({
      date: format(new Date(), "yyyy-MM-dd"),
      time: format(new Date(), "HH:mm"),
      medicalNoteType: "2",
      noteDescription: "",
      weightMeasurement: "",
      weightUnit: "kg",
      heightMeasurement: "",
      heightUnit: "m",
      temperatureMeasurement: "",
      temperatureUnit: "celcius",
      pulseMeasurement: "",
      respirationMeasurement: "",
    });
  }, [reset]);

  const handleEdit = (noteId: string) => {
    const selected = notes.find((note) => String(note.medicalNoteId) === noteId);
    if (!selected) return;
    const parsed = parseMedicalNoteDescription(
      selected.medicalNoteType,
      selected.noteDescription
    );
    setSelectedNoteId(selected.medicalNoteId);
    reset({
      date: toDatePart(selected.createdOn_UTC),
      time: toTimePart(selected.createdOn_UTC),
      medicalNoteType: String(selected.medicalNoteType),
      noteDescription: parsed.noteDescription,
      weightMeasurement: parsed.weightMeasurement,
      weightUnit: parsed.weightUnit,
      heightMeasurement: parsed.heightMeasurement,
      heightUnit: parsed.heightUnit,
      temperatureMeasurement: parsed.temperatureMeasurement,
      temperatureUnit: parsed.temperatureUnit,
      pulseMeasurement: parsed.pulseMeasurement,
      respirationMeasurement: parsed.respirationMeasurement,
    });
  };

  const handleDelete = (noteId: string) => {
    if (!Number.isFinite(Number(noteId)) || Number(noteId) <= 0) return;
    setPendingDeleteNoteId(Number(noteId));
  };

  const confirmDelete = async () => {
    if (orgId <= 0 || pendingDeleteNoteId === null || pendingDeleteNoteId <= 0) return;

    setDeleting(true);
    try {
      const token = await getAccessTokenSilently();
      const { error: apiError } = await deleteMedicalNote(token, orgId, pendingDeleteNoteId);
      if (apiError) {
        vgToast.error(apiError.message);
        return;
      }
      vgToast.success("Medical note deleted.");
      await load();
      if (selectedNoteId === pendingDeleteNoteId) {
        resetToCreateMode();
      }
      setPendingDeleteNoteId(null);
    } catch (e) {
      vgToast.error((e as Error).message);
    } finally {
      setDeleting(false);
    }
  };

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    if (orgId <= 0 || medicalRecordId <= 0) {
      vgToast.error("Missing organisation or medical record id.");
      return;
    }

    const medicalNoteType = Number(data.medicalNoteType) || 2;
    const noteDescription = buildMedicalNoteDescription({
      medicalNoteType,
      noteDescription: data.noteDescription,
      weightMeasurement: data.weightMeasurement,
      weightUnit: data.weightUnit,
      heightMeasurement: data.heightMeasurement,
      heightUnit: data.heightUnit,
      temperatureMeasurement: data.temperatureMeasurement,
      temperatureUnit: data.temperatureUnit,
      pulseMeasurement: data.pulseMeasurement,
      respirationMeasurement: data.respirationMeasurement,
    });
    const createdOn_UTC = buildCreatedOnUtc(data.date, data.time);

    setSubmitting(true);
    try {
      const token = await getAccessTokenSilently();
      const { error: apiError } = selectedNoteId
        ? await updateMedicalNote(token, {
            orgId,
            medicalRecordId,
            medicalNoteId: selectedNoteId,
            medicalNoteType,
            noteDescription,
            createdOn_UTC,
          })
        : await createMedicalNote(token, {
            orgId,
            medicalRecordId,
            medicalNoteType,
            noteDescription,
            createdOn_UTC,
          });

      if (apiError) {
        vgToast.error(apiError.message);
        return;
      }

      vgToast.success(selectedNoteId ? "Medical note saved." : "Medical note created.");
      await load();
      resetToCreateMode();
    } catch (e) {
      vgToast.error((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const selectedType = Number(watch("medicalNoteType")) || 2;

  // Client-side paging: the medical record returns every note at once.
  const view = useTableView(React.useMemo(() => notes.map(formatRow), [notes]));

  return (
    <>
      <div className="grid grid-cols-1 gap-6 items-start xl:grid-cols-[minmax(0,1.2fr)_minmax(22rem,0.8fr)]">
        <div>
        <div className="flex items-center justify-between text-sm text-gray-400 m-4">
          <PageSizeSelect value={view.pageSize} onChange={view.setPageSize} />
          <Button color="Primary" onClick={resetToCreateMode}>
            <span>Create New Entry</span>
          </Button>
        </div>

        <Table<Row>
          loading={loading}
          columns={{
            date: "Date",
            type: "Note Type",
            description: "Note Description",
          }}
          rows={view.pageRows}
          sort={view.sort}
          onSortChange={view.toggleSort}
          handleViewItem={handleEdit}
          handleRemoveItem={handleDelete}
        />

        {loading && (
          <div className="px-4 pb-2 text-sm text-gray-500">Loading medical notes…</div>
        )}
        {error && <div className="px-4 pb-2 text-sm text-red-500">{error}</div>}

        <PaginationBar
          from={view.from}
          to={view.to}
          total={view.total}
          currentPage={view.page}
          totalPages={view.totalPages}
          onPrev={view.goPrev}
          onNext={view.goNext}
          onPageSelect={view.goToPage}
        />
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="border border-gray-200 rounded-2xl p-5 bg-white shadow-sm">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {selectedNoteId ? "Edit Medical Note" : "New Medical Note"}
              </h3>
              <p className="text-sm text-gray-500">
                {selectedNoteId
                  ? "Update the selected patient record."
                  : "Create a new medical note for this patient."}
              </p>
            </div>
            {selectedNoteId && (
              <Button color="Secondary" onClick={resetToCreateMode}>
                <span>Cancel Edit</span>
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 mb-4 sm:grid-cols-2">
            <Input type="date" {...register("date")} label="Date" />
            <Input type="text" {...register("time")} label="Time" placeholder="HH:mm" />
          </div>

          <div className="mb-4">
            <Select
              {...register("medicalNoteType")}
              label="Medical Note Type"
              options={toSelectOptions(medicalNoteTypeOptions)}
            />
          </div>

          {isWeightNote(selectedType) && (
            <div className="grid grid-cols-1 gap-4 mb-4 sm:grid-cols-2">
              <Input
                type="number"
                {...register("weightMeasurement")}
                label="Weight Measurement"
                placeholder="Enter weight"
              />
              <Select
                {...register("weightUnit")}
                label="Weight Units"
                options={toSelectOptions(weightUnitOptions)}
              />
            </div>
          )}

          {isHeightNote(selectedType) && (
            <div className="grid grid-cols-1 gap-4 mb-4 sm:grid-cols-2">
              <Input
                type="number"
                {...register("heightMeasurement")}
                label="Height Measurement"
                placeholder="Enter height"
              />
              <Select
                {...register("heightUnit")}
                label="Height Units"
                options={toSelectOptions(heightUnitOptions)}
              />
            </div>
          )}

          {isTemperatureNote(selectedType) && (
            <div className="grid grid-cols-1 gap-4 mb-4 sm:grid-cols-2">
              <Input
                type="number"
                {...register("temperatureMeasurement")}
                label="Temperature Measurement"
                placeholder="Enter temperature"
              />
              <Select
                {...register("temperatureUnit")}
                label="Temperature Units"
                options={toSelectOptions(temperatureUnitOptions)}
              />
            </div>
          )}

          {isPulseNote(selectedType) && (
            <div className="mb-4">
              <Input
                type="number"
                {...register("pulseMeasurement")}
                label="Pulse Measurement"
                placeholder="Beats per minute"
              />
            </div>
          )}

          {isRespirationNote(selectedType) && (
            <div className="mb-4">
              <Input
                type="number"
                {...register("respirationMeasurement")}
                label="Respiration Measurement"
                placeholder="Breaths per minute"
              />
            </div>
          )}

          {!isWeightNote(selectedType) &&
            !isHeightNote(selectedType) &&
            !isTemperatureNote(selectedType) &&
            !isPulseNote(selectedType) &&
            !isRespirationNote(selectedType) && (
              <div className="mb-4">
                <Text
                  {...register("noteDescription")}
                  label="Medical Note Details"
                  placeholder="Enter note details"
                  rows={6}
                />
              </div>
            )}

          <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-3 text-sm text-gray-500">
            Attachments are not available in this version of the patient medical-note editor.
          </div>

          <div className="flex justify-end gap-3 mt-5">
            <Button color="Secondary" onClick={resetToCreateMode} disabled={submitting || deleting}>
              <span>Reset</span>
            </Button>
            <Button color="Primary" type="submit" disabled={submitting || deleting}>
              <span>{submitting ? "Saving…" : selectedNoteId ? "Update Entry" : "Create Entry"}</span>
            </Button>
          </div>
        </form>
      </div>
      <ConfirmDialog
        open={pendingDeleteNoteId !== null}
        title="Delete medical note?"
        description="This will permanently remove the selected medical note from the patient's record."
        confirmLabel="Delete note"
        busy={deleting}
        onCancel={() => {
          if (!deleting) setPendingDeleteNoteId(null);
        }}
        onConfirm={confirmDelete}
      />
    </>
  );
};

export default MedicalNotesList;
